import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Download, Copy, Pencil, Check, X, Eye, FilePlus2, CornerDownRight } from 'lucide-react';
import { useQuotation, useQuotations } from '@/composables/financial/useQuotations';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { quotationService } from '@/services/quotation.service';
import { usePdfActions } from '@/composables/shared/usePdfActions';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { CreateInvoiceDialog } from '@/components/quotations/CreateInvoiceDialog';
import { groupItems } from '@/lib/documentItems';
import { taxLines } from '@/lib/tax';

const EDITABLE = new Set(['DRAFT', 'SENT']);
// Billing states: accepted, and however far its invoices have got.
const BILLING = new Set(['ACCEPTED', 'PARTIALLY_INVOICED', 'INVOICED', 'PARTIALLY_PAID', 'PAID', 'CONVERTED']);
const TERMINAL = new Set([...BILLING, 'REJECTED', 'EXPIRED', 'CANCELLED']);

function effectiveStatus(status: string, validUntil?: string) {
  if (status === 'SENT' && validUntil && new Date(validUntil).getTime() < Date.now()) return 'EXPIRED';
  return status;
}

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { quotation, isLoading } = useQuotation(id!);
  const { sendQuotation, acceptQuotation, rejectQuotation, duplicateQuotation, createInvoiceFromQuotation } = useQuotations();
  const { settings } = useOrganizationSettings();
  const [confirmAccept, setConfirmAccept] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  const pdf = usePdfActions();
  const handleDownloadPdf = () => pdf.download(() => quotationService.downloadPdf(id!), `quotation-${quotation?.quotationNumber || id}.pdf`);
  const handleViewPdf = () => pdf.view(() => quotationService.downloadPdf(id!));

  const handleAccept = () => {
    setConfirmAccept(false);
    acceptQuotation.mutate(id!);
  };

  if (isLoading) return <LoadingSpinner />;
  if (!quotation) return <div className="p-8 text-center">Quotation not found</div>;

  // Lump-sum: the items are description-only, so the money columns are hidden.
  const isLumpSum = quotation.pricingMode === 'LUMP_SUM';

  const shownStatus = effectiveStatus(quotation.status, quotation.validUntil);
  const canEdit = EDITABLE.has(quotation.status);
  const isTerminal = TERMINAL.has(quotation.status);

  // Billing roll-up. The button goes once the invoices reach the quoted total.
  const inBilling = BILLING.has(quotation.status);
  const invoices = quotation.invoices ?? [];
  const total = Number(quotation.totalAmount) || 0;
  const invoiced = Number(quotation.invoicedAmount) || 0;
  const paid = Number(quotation.paidAmount) || 0;
  const remaining = Math.max(0, total - invoiced);
  const canInvoice = inBilling && remaining > 0.01;
  const lines = groupItems(quotation.items);
  const money = (v: number | string | undefined) => formatCurrency(v ?? 0, quotation.currency);

  return (
    <PageContainer backTo
      title={`Quotation ${quotation.quotationNumber}`}
      actions={
        <>
          <Button variant="outline" onClick={handleViewPdf} loading={pdf.isViewing}><Eye className="mr-2 h-4 w-4" />View PDF</Button>
          <Button variant="outline" onClick={handleDownloadPdf} loading={pdf.isDownloading}><Download className="mr-2 h-4 w-4" />Download</Button>
          {canEdit && <Button variant="outline" onClick={() => navigate(`/financial/quotations/${quotation.id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
          {quotation.status === 'DRAFT' && <Button onClick={() => sendQuotation.mutate(quotation.id)}><Send className="mr-2 h-4 w-4" />Send</Button>}
          <Button variant="outline" onClick={() => duplicateQuotation.mutate(quotation.id)}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
          {!isTerminal && (
            <>
              <Button variant="outline" onClick={() => setConfirmReject(true)}><X className="mr-2 h-4 w-4" />Reject</Button>
              <Button onClick={() => setConfirmAccept(true)}><Check className="mr-2 h-4 w-4" />Accept</Button>
            </>
          )}
          {canInvoice && <Button onClick={() => setInvoiceDialogOpen(true)}><FilePlus2 className="mr-2 h-4 w-4" />Create invoice</Button>}
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Quotation Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={shownStatus} type="quotation" /></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-bold text-lg">{formatCurrency(quotation.totalAmount, quotation.currency)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Issue Date</span><span>{formatDate(quotation.issueDate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Valid Until</span><span>{formatDate(quotation.validUntil)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="text-right">
              {quotation.billingCompanyName && <div className="font-medium">{quotation.billingCompanyName}</div>}
              <div className={quotation.billingCompanyName ? 'text-xs text-muted-foreground' : ''}>{quotation.billingName || '-'}</div>
            </span></div>
            {quotation.billingTaxId && (
              <div className="flex justify-between"><span className="text-muted-foreground">Client GSTIN</span><span className="font-mono text-sm">{quotation.billingTaxId}</span></div>
            )}
            {inBilling && (
              <div className="space-y-2 border-t pt-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoiced</span><span>{money(invoiced)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span>{money(paid)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Left to invoice</span><span className="font-medium">{money(remaining)}</span></div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    {!isLumpSum && <>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map(({ item, children }) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.description}
                        {children.map((c) => (
                          <div key={c.id} className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><CornerDownRight className="h-3 w-3 shrink-0" />{c.description}</div>
                        ))}
                      </TableCell>
                      {!isLumpSum && <>
                        <TableCell className="text-right align-top">{item.quantity}</TableCell>
                        <TableCell className="text-right align-top">{formatCurrency(item.unitPrice, quotation.currency)}</TableCell>
                        <TableCell className="text-right align-top">{formatCurrency(item.amount, quotation.currency)}</TableCell>
                      </>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 space-y-1 text-right">
                <p className="text-sm">Subtotal: {formatCurrency(quotation.subtotal, quotation.currency)}</p>
                {taxLines(quotation, settings?.taxLabel).map((line) => (
                  <p key={line.label} className="text-sm">{line.label}: {formatCurrency(line.amount, quotation.currency)}</p>
                ))}
                {Number(quotation.discountAmount) > 0 && <p className="text-sm">Discount: -{formatCurrency(quotation.discountAmount, quotation.currency)}</p>}
                <p className="text-lg font-bold">Total: {formatCurrency(quotation.totalAmount, quotation.currency)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Invoices</CardTitle>
              {canInvoice && <Button size="sm" variant="outline" onClick={() => setInvoiceDialogOpen(true)}><FilePlus2 className="mr-2 h-4 w-4" />Create invoice</Button>}
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {inBilling
                    ? 'Nothing invoiced yet. Raise the whole quotation as one invoice, or bill it in stages.'
                    : 'Invoices are raised once the client accepts this quotation.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id} className={`cursor-pointer ${inv.status === 'CANCELLED' ? 'opacity-60' : ''}`} onClick={() => navigate(`/financial/invoices/${inv.id}`)}>
                        <TableCell className="font-medium text-primary">{inv.invoiceNumber}</TableCell>
                        <TableCell>{inv.issueDate ? formatDate(inv.issueDate) : '-'}</TableCell>
                        <TableCell><StatusBadge status={inv.status} type="invoice" /></TableCell>
                        <TableCell className="text-right">{money(inv.totalAmount)}</TableCell>
                        <TableCell className="text-right">{money(inv.paidAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {invoices.length > 0 && (
                <p className="mt-3 text-right text-sm text-muted-foreground">
                  {money(invoiced)} of {money(total)} invoiced{remaining > 0.01 ? ` · ${money(remaining)} left` : ' · fully invoiced'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <CreateInvoiceDialog
        quotation={quotation}
        settings={settings}
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
        isSubmitting={createInvoiceFromQuotation.isPending}
        onSubmit={(payload) => createInvoiceFromQuotation.mutate(
          { id: quotation.id, data: payload },
          {
            // Land on the draft so it can be reviewed (payment details, notes) before it goes out.
            onSuccess: (res) => {
              setInvoiceDialogOpen(false);
              if (res?.data?.id) navigate(`/financial/invoices/${res.data.id}`);
            },
          },
        )}
      />

      <ConfirmDialog
        open={confirmAccept}
        onOpenChange={setConfirmAccept}
        title="Accept Quotation?"
        description="Marks the quotation as accepted by the client. Invoices are raised from it afterwards with Create invoice — the whole quotation at once, or in stages."
        confirmLabel="Accept"
        onConfirm={handleAccept}
        isLoading={acceptQuotation.isPending}
      />

      <ConfirmDialog
        open={confirmReject}
        onOpenChange={setConfirmReject}
        title="Reject Quotation?"
        description="This marks the quotation as REJECTED. You won't be able to send or accept it after this."
        variant="destructive"
        confirmLabel="Reject"
        onConfirm={() => { setConfirmReject(false); rejectQuotation.mutate(quotation.id); }}
        isLoading={rejectQuotation.isPending}
      />

    </PageContainer>
  );
}
